import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, } from "@/components/ui/table"
import { useEffect, useMemo, useRef, useState } from "react"
import { ColumnDef, flexRender, getCoreRowModel, getFilteredRowModel, getPaginationRowModel, getSortedRowModel, PaginationState, SortingState, VisibilityState, useReactTable, TableOptions, ColumnSort, ColumnSizingState, } from "@tanstack/react-table"
import { ChevronDownIcon, ChevronUpIcon, GripVerticalIcon, ChevronsUpDownIcon, MoreHorizontal, ChevronFirstIcon, ChevronLeftIcon, ChevronRightIcon, ChevronLastIcon, Columns3, Settings2, SearchIcon, RefreshCcw, Check, Download } from "lucide-react"
import { CSSProperties, useId } from "react"
import { closestCenter, DndContext, KeyboardSensor, MouseSensor, TouchSensor, useSensor, useSensors, type DragEndEvent, } from "@dnd-kit/core"
import { restrictToHorizontalAxis } from "@dnd-kit/modifiers"
import { arrayMove, horizontalListSortingStrategy, SortableContext, useSortable, } from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import { Cell, Header, } from "@tanstack/react-table"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Pagination, PaginationContent, PaginationItem } from "@/components/ui/pagination"
import { Label } from "@/components/ui/label"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuCheckboxItem, DropdownMenuSeparator, DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger } from "@/components/ui/dropdown-menu"
import { Input } from "@/components/ui/input"
import { EmptyData } from "./empty"
import React from "react"
import { Skeleton } from "../ui/skeleton"
import { format } from 'date-fns'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../ui/accordion"
import { useNavigate } from "react-router-dom"
import { ToolbarItem } from "./toolbaritem"
import { DeclarativeMenu } from "../declarativemenu"
import { type TableController } from "./controller"
import { useForceUpdate } from "@/lib/utils"
import { app } from "@/lib/app"
import { toast } from "sonner"
import { ExportDialog } from "./exportdialog"
import { ExportColumn, ExportOptions, exportRows, sanitizeFileName } from "./export"


declare global {
  type Column<T, M> = ColumnDef<T, M>
  type Columns<T = any> = { [key in keyof T]?: Column<T, T[key]> }
  type PaginationData = PaginationState
}
export interface DataTableProps {
  data?: any[]
  children?: any
  columns?: Columns<any>
  showSelection?: boolean
  onRowClick?: ((row: any) => void) | string
  onLoad?: (state: PaginationState) => Promise<any[]>
  onPaginationChange?: (state: PaginationState) => void
  showSearch?: boolean
  showColumnSelection?: boolean
  showPagination?: boolean
  total?: number
  loading?: boolean
  sort?: [string, "asc" | "desc"]
  onSortingChange?: (field: string, direction: "asc" | "desc" | undefined) => void
  actions?: DropdownMenuContents | ((row: any) => DropdownMenuContents)
  onActionExecuted?: () => Promise<void>
  rowSelection?: Record<string, boolean>
  onRowSelectionChange?: (selection: Record<string, boolean>) => void
  onSelectedItemsChange?: (selected: any[]) => void
  controller?: TableController
  onGlobalFilterChange?: (filter: string) => void
  enableServerSideFiltering?: boolean
  getRowClassName?: (row: any) => string | undefined
  configKey?: string
  showExport?: boolean
  exportFileName?: string
  exportMaxRows?: number
  onExportLoadAll?: (maxRows: number) => Promise<any[]>
}



export function DataTable(props: DataTableProps) {

  let {
    columns,
    onRowClick,
    loading,
    showSearch = true,
    showColumnSelection = true,
    showPagination = true,
    sort,
    actions,
    onActionExecuted,
    getRowClassName,
    configKey,
    showExport = true,
    exportMaxRows = 5000,
  } = props


  let hideActions = !actions
  let hideSelection = !props.showSelection

  const sortingState: SortingState = sort ? [{ id: sort[0], desc: sort[1] == "desc" }] : []

  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: 20, })
  const [data, setData] = useState<any[]>(props.data)
  let [isLoading, setIsLoading] = useState(loading)

  if (loading != null) isLoading = loading // This is in case there is an external loading indicator

  const state = useRef({
    savedState: null as UIStateDatagrid
  })

  if (!state.current.savedState) {
    if (configKey) {
      if (!app.uiState.datagrids?.[configKey]) app.uiState.datagrids[configKey] = { columns: {} }
      state.current.savedState = app.uiState.datagrids[configKey]
      state.current.savedState.columns ||= {}
    }
  }




  const cols = useMemo(() => {
    const saved = state.current.savedState
    const gen: ColumnDef<any>[] = []
    Object.entries(columns).forEach(([k, v]) => {
      const cd = { ...(v as any) } as ColumnDef<any>
      cd.id ||= k
      cd["accessorKey"] ||= cd.id

      if (saved) {
        // console.log(saved)
        if (saved.columns[cd.id]) {
          cd.size = saved.columns[cd.id].width || cd.size
        }
      }
      gen.push(cd)
    })
    return gen
  }, [columns])

  const [version, setVersion] = useState(0)
  const [searchQuery, setSearchQuery] = useState<string>()
  const [search, setSearch] = useState<string>("")
  const [sorting, setSorting] = useState<SortingState>(sortingState)
  const [columnOrder, setColumnOrder] = useState<string[]>(cols.map((column) => column.id as string))
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({})
  const [rowSelection, setRowSelection] = useState(props.rowSelection || {})
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>({})
  const [exportOpen, setExportOpen] = useState(false)
  const navigate = useNavigate()
  const sensors = useSensors(useSensor(MouseSensor, {}), useSensor(TouchSensor, {}), useSensor(KeyboardSensor, {}))

  const validColumnIds = useMemo(() => new Set(cols.map(c => String(c.id))), [cols])

  // All columns, hidden ones included, in the order the user currently has them
  const exportColumns = useMemo<ExportColumn[]>(() => {
    const ids = columnOrder.length > 0 ? columnOrder : cols.map(c => String(c.id))
    return ids.map(id => {
      const def = cols.find(c => String(c.id) === id)
      return { id, header: typeof def?.header === "string" ? def.header : id }
    })
  }, [columnOrder, cols])

  const sortingForTable = useMemo(() => {
    const filtered = sorting.filter(s => validColumnIds.has(s.id))
    if (filtered.length > 0) return filtered
    if (sort && validColumnIds.has(String(sort[0]))) {
      return [{ id: String(sort[0]), desc: sort[1] === "desc" }]
    }
    return []
  }, [sorting, validColumnIds, sort])

  function getSelectedItems() {
    return Object.keys(rowSelection).map((id) => data.find((item) => item.id == id)).filter((i) => i !== undefined)
  }

  if (props.controller) {
    props.controller.getSeleted = () => {
      return getSelectedItems()
    }
    if (!props.controller.reload) {
      props.controller.reload = () => {
        setVersion(version + 1)
      }
    }
    // props.controller.reload = () => {
    //   setVersion(version + 1)
    // }
    // props.controller.setLoading = (v) => {
    //   setIsLoading(v)
    // }
    if (!props.controller.setLoading) {
      props.controller.setLoading = (v) => {
        setIsLoading(v)
      }
    }
  }

  useEffect(() => {
    async function loadData() {
      setIsLoading(true)
      let data = await props.onLoad(pagination)
      if (!data) data = []
      setIsLoading(false)
      setData(data)
    }
    if (props.onLoad) loadData()
  }, [version])

  useEffect(() => {
    setData(props.data || [])
  }, [props.data])

  useEffect(() => {
    if (props.rowSelection !== undefined) {
      setRowSelection(props.rowSelection)
    }
  }, [props.rowSelection])

  useEffect(() => {
    if (props.onRowSelectionChange) {
      props.onRowSelectionChange(rowSelection)
    }
    if (props.onSelectedItemsChange) {
      props.onSelectedItemsChange(getSelectedItems())
    }
  }, [rowSelection])

  useEffect(() => {
    if (props.onPaginationChange) {
      props.onPaginationChange(pagination)
    }
  }, [pagination.pageIndex, pagination.pageSize])


  function onSortingChange(updater: SortingState | ((old: SortingState) => SortingState)) {
    setSorting(prev => {
      const prevBase = prev.filter(s => validColumnIds.has(s.id))
      const baseForUpdater =
        prevBase.length > 0
          ? prevBase
          : sort && validColumnIds.has(String(sort[0]))
            ? [{ id: String(sort[0]), desc: sort[1] === "desc" }]
            : []
      const next = typeof updater === "function" ? (updater as (old: SortingState) => SortingState)(baseForUpdater) : updater
      if (props.onSortingChange) {
        if (next[0]) {
          props.onSortingChange(next[0].id, next[0].desc === true ? "desc" : "asc")
        } else {
          props.onSortingChange(undefined, undefined)
        }
      }
      return next
    })
  }

  function handleGlobalFilterChange(search: string) {
    setSearch(search)
    if (props.onGlobalFilterChange) {
      props.onGlobalFilterChange(search)
    }
  }

  const options: TableOptions<any> = {
    data: data || [],
    columns: cols,
    columnResizeMode: "onChange",
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: props.enableServerSideFiltering ? undefined : getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    onSortingChange,
    onColumnVisibilityChange: setColumnVisibility,
    onGlobalFilterChange: handleGlobalFilterChange,
    state: {
      sorting: sortingForTable,
      columnOrder,
      columnVisibility,
      rowSelection,
      pagination,
      globalFilter: search,
      columnSizing,
    },
    onColumnOrderChange: setColumnOrder,
    onRowSelectionChange: setRowSelection,
    enableRowSelection: true,
    enableSortingRemoval: true,
    getRowId: (row) => row.id || String(Math.random()),
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
    onColumnSizingChange: (v: any) => {
      try {
        const newSizing = typeof v === 'function' ? v(columnSizing) : v
        // console.log('New column sizing state:', newSizing)
        setColumnSizing(newSizing)
        const tableState = state.current.savedState
        if (newSizing && table && tableState && tableState.columns) {
          for (const key in newSizing) {
            if (!tableState.columns[key]) tableState.columns[key] = {}
            if (typeof tableState.columns[key] !== "object") tableState.columns[key] = {}
            const w = newSizing[key]
            tableState.columns[key].width = w
          }
        }
      } catch (error) {

      }
    }
  }

  if (props.total !== undefined) {
    delete options.getPaginationRowModel
    options.manualPagination = true
    options.rowCount = props.total
  }

  const table = useReactTable(options)

  let Empty = <EmptyData />
  const selectionColumnWidth = !hideSelection ? 48 : 0 // 48px for w-12
  const actionsColumnWidth = !hideActions ? 48 : 0 // 48px for w-12
  const rows = table.getRowModel().rows || []
  const allCheckedStatus = table.getIsAllPageRowsSelected() ? true : table.getIsSomePageRowsSelected() ? "indeterminate" : false

  function handleDragEnd({ active, over }: DragEndEvent) {
    if (active && over && active.id !== over.id) {
      setColumnOrder((columnOrder) => {
        const oldIndex = columnOrder.indexOf(active.id as string)
        const newIndex = columnOrder.indexOf(over.id as string)
        return arrayMove(columnOrder, oldIndex, newIndex)
      })
    }
  }

  async function handleExport(options: ExportOptions) {
    try {
      let exportData: any[] = []

      if (options.scope === "selected") exportData = getSelectedItems()
      else if (options.scope === "all") exportData = props.onExportLoadAll ? await props.onExportLoadAll(options.maxRows) : (data || []).slice(0, options.maxRows)
      else exportData = rows.map(r => r.original)

      if (!exportData || exportData.length === 0) {
        toast.error("There is nothing to export")
        return
      }

      await exportRows(exportData, options, sanitizeFileName(props.exportFileName || configKey || "export"))
    } catch (error) {
      console.log("Export error", error)
      toast.error("The export failed")
    }
  }

  function onRowClicked(v: any) {
    if (onRowClick) {
      if (typeof onRowClick === "string") {
        let finalPath = onRowClick.trim()
        if (!finalPath.endsWith("/")) finalPath = `${finalPath}/`
        if (v["id"]) navigate(`${finalPath}${v["id"]}`)
      } else {
        onRowClick(v)
      }
    }
  }

  const tbitems = []

  if (props.children) {
    React.Children.forEach(props.children, (child) => {
      if (!React.isValidElement(child)) return
      if (child.type === EmptyData) Empty = child as any
      if (child.type === ToolbarItem) tbitems.push(child)
    })
  }

  let tbody = Empty


  if (isLoading) {
    tbody = <TableBody>
      {Array.from({ length: pagination.pageSize }, (_, i) => i + 1).map((row, id) => (
        <TableRow key={id}>
          {table.getAllColumns().map((cell, i) => (
            <TableCell key={i} className="truncate px-2 py-2" >
              <Skeleton className="h-8 w-full rounded-md" />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </TableBody>
  } else {
    if (rows.length > 0) {
      tbody = <TableBody>
        {rows.map((row) => {
          const rowClassName = getRowClassName ? getRowClassName(row.original) : undefined
          const className = [onRowClick ? "cursor-pointer" : undefined, rowClassName].filter(Boolean).join(" ")
          return (
            <TableRow key={row.id} data-state={row.getIsSelected() && "selected"} onClick={() => onRowClicked(row.original)} className={className || undefined}>

              {!hideSelection && <TableCell className="p-0 w-12 sticky left-0 bg-white z-10" onClick={(e) => e.stopPropagation()} >
                <div className="w-full h-full border-r flex items-center justify-center">
                  <Checkbox checked={row.getIsSelected()} onCheckedChange={(value) => row.toggleSelected(!!value)} aria-label="Select row" />
                </div>
              </TableCell>}

              {row.getVisibleCells().map((cell, idx, arr) => (
                <SortableContext key={cell.id} items={columnOrder} strategy={horizontalListSortingStrategy}>
                  <DragAlongCell key={cell.id} cell={cell} />
                </SortableContext>
              ))}

              {!hideActions && <TableCell className="p-0 w-12" onClick={(e) => e.stopPropagation()} >
                <div className="w-full h-full border-l flex items-center justify-center">
                  <DeclarativeMenu menu={typeof actions === 'function' ? actions(row.original) : actions} reference={row.original} onActionExecuted={async () => {
                    console.log("Action in datatable")

                    await onActionExecuted()
                  }} >
                    <Button size="icon" variant="ghost" className="size-6">
                      <MoreHorizontal size={16} />
                    </Button>
                  </DeclarativeMenu>
                </div>
              </TableCell>}

              {/* {!hideActions && <TableCell className="p-0 pl-4 w-10 sticky right-0 bg-background z-10" onClick={(e) => e.stopPropagation()} >
              <div className="absolute top-0 right-0 bottom-0 w-full h-full border-l pl-3 pt-2">
                <Button size="icon" variant="ghost" className="size-6">
                  <MoreHorizontal size={16} />
                </Button>
              </div>
            </TableCell>}
          */}
            </TableRow>
          )
        })}
      </TableBody>
    }
  }

  return <div className="grid grid-rows-[auto_100fr_1fr] w-full h-full pb-4 min-h-0">

    <div className="pb-2 flex items-center gap-2">

      {showSearch && (
        <div className="relative w-160 max-w-full">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search..."
            value={search ?? ""}
            onChange={(event) => {
              table.setGlobalFilter(event.target.value)
            }}
            className="h-8 pl-8 rounded-lg w-full"
          />
        </div>
      )}

      <div className="grow"></div>

      {React.Children.toArray(tbitems)}


      {(showExport || showColumnSelection) && <div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="ml-auto">
              <Settings2 />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">

            {showColumnSelection && <DropdownMenuSub>
              <DropdownMenuSubTrigger>
                <Columns3 /> Columns
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                <div className="relative">
                  <Input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-8" placeholder="Search" onKeyDown={(e) => e.stopPropagation()} />
                  <SearchIcon className="absolute inset-y-0 my-auto left-2 h-4 w-4" />
                </div>
                <DropdownMenuSeparator />
                {table.getAllColumns().filter((column) => column.getCanHide()).map((column) => {
                  if (searchQuery && !column.id.toLowerCase().includes(searchQuery.toLowerCase())) return null
                  return <DropdownMenuCheckboxItem
                    key={column.id}
                    className="capitalize"
                    checked={column.getIsVisible()}
                    onCheckedChange={(value) => column.toggleVisibility(!!value)}
                    onSelect={(e) => e.preventDefault()}
                  >
                    {column.id}
                  </DropdownMenuCheckboxItem>
                })}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => {
                  table.resetColumnVisibility()
                  setSearchQuery("")
                }}>
                  <RefreshCcw /> Reset
                </DropdownMenuItem>
              </DropdownMenuSubContent>
            </DropdownMenuSub>}

            {showExport && showColumnSelection && <DropdownMenuSeparator />}

            {showExport && <DropdownMenuItem disabled={isLoading} onClick={() => setExportOpen(true)}>
              <Download /> Export...
            </DropdownMenuItem>}

          </DropdownMenuContent>
        </DropdownMenu>
      </div>}

    </div>


    <DndContext id={useId()} collisionDetection={closestCenter} modifiers={[restrictToHorizontalAxis]} onDragEnd={handleDragEnd} sensors={sensors}>
      <Table className="table-fixed" style={{ width: table.getCenterTotalSize() + selectionColumnWidth + actionsColumnWidth }}>
        <TableHeader>
          <TableRow className="*:whitespace-nowrap sticky top-0 bg-white z-30" >
            {!hideSelection && <TableHead className="w-12 p-0 sticky left-0 bg-white z-20 border-r h-8 whitespace-nowrap">
              <div className="w-full h-full flex items-center justify-center">
                <Checkbox checked={allCheckedStatus} onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)} aria-label="Select all" />
              </div>
            </TableHead>}
            <SortableContext items={columnOrder} strategy={horizontalListSortingStrategy}>
              {table.getFlatHeaders().map((header) => <DraggableTableHeader key={header.id} header={header} />)}
            </SortableContext>
            {!hideActions && <TableHead className="w-12 border-l h-8 whitespace-nowrap">
            </TableHead>}
          </TableRow>
        </TableHeader>
        {tbody}
      </Table>
    </DndContext>

    {showPagination && <div className="flex items-center justify-between gap-4 pt-2">

      <div className="flex items-center">
        <Label className="max-sm:sr-only">
          Rows per page
        </Label>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button type="button" variant="ghost" className="w-fit whitespace-nowrap">
              {table.getState().pagination.pageSize}
              <ChevronDownIcon className="size-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent>
            <DropdownMenuItem onClick={() => table.setPageSize(10)}>10</DropdownMenuItem>
            <DropdownMenuItem onClick={() => table.setPageSize(20)}>20</DropdownMenuItem>
            <DropdownMenuItem onClick={() => table.setPageSize(30)}>30</DropdownMenuItem>
            <DropdownMenuItem onClick={() => table.setPageSize(40)}>40</DropdownMenuItem>
            <DropdownMenuItem onClick={() => table.setPageSize(50)}>50</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="text-muted-foreground flex grow justify-end text-sm whitespace-nowrap">
        <p className="text-muted-foreground text-sm whitespace-nowrap" aria-live="polite">
          <span className="text-foreground">
            {table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1}
            -
            {Math.min(Math.max(table.getState().pagination.pageIndex * table.getState().pagination.pageSize + table.getState().pagination.pageSize, 0), table.getRowCount())}
          </span>
          {" of "}
          <span className="text-foreground">
            {table.getRowCount().toString()}
          </span>
        </p>
      </div>

      <div>
        <Pagination>
          <PaginationContent>
            <PaginationItem>
              <Button type="button" size="icon" variant="outline" className="disabled:pointer-events-none disabled:opacity-50" onClick={() => table.firstPage()} disabled={!table.getCanPreviousPage() || loading} aria-label="Go to first page">
                <ChevronFirstIcon size={16} aria-hidden="true" />
              </Button>
            </PaginationItem>
            <PaginationItem>
              <Button type="button" size="icon" variant="outline" className="disabled:pointer-events-none disabled:opacity-50" onClick={() => table.previousPage()} disabled={!table.getCanPreviousPage() || loading} aria-label="Go to previous page">
                <ChevronLeftIcon size={16} aria-hidden="true" />
              </Button>
            </PaginationItem>
            <PaginationItem>
              <Button type="button" size="icon" variant="outline" className="disabled:pointer-events-none disabled:opacity-50" onClick={() => table.nextPage()} disabled={!table.getCanNextPage() || loading} aria-label="Go to next page">
                <ChevronRightIcon size={16} aria-hidden="true" />
              </Button>
            </PaginationItem>
            <PaginationItem>
              <Button type="button" size="icon" variant="outline" className="disabled:pointer-events-none disabled:opacity-50" onClick={() => table.lastPage()} disabled={!table.getCanNextPage() || loading} aria-label="Go to last page">
                <ChevronLastIcon size={16} aria-hidden="true" />
              </Button>
            </PaginationItem>
          </PaginationContent>
        </Pagination>
      </div>
    </div>}

    {showExport && <ExportDialog
      open={exportOpen}
      onOpenChange={setExportOpen}
      columns={exportColumns}
      initialVisible={table.getVisibleLeafColumns().map(c => c.id)}
      hasSelection={Object.keys(rowSelection).length > 0}
      canExportAll={!!props.onExportLoadAll || props.total === undefined}
      totalRows={props.total !== undefined ? props.total : (data || []).length}
      defaultMaxRows={exportMaxRows}
      onExport={handleExport}
    />}

  </div>


}


function DraggableTableHeader({ header }: { header: Header<any, unknown> }) {

  const { attributes, isDragging, listeners, setNodeRef, transform, transition, } = useSortable({ id: header.column.id, })

  const isSorted = header.column.getIsSorted()
  const { isPlaceholder, column } = header

  const style: CSSProperties = {
    opacity: isDragging ? 0.8 : 1,
    position: "relative",
    transform: CSS.Translate.toString(transform),
    transition,
    whiteSpace: "nowrap",
    width: header.column.getSize(),
    zIndex: isDragging ? 1 : 0,
  }

  return <TableHead ref={setNodeRef} className="group relative h-8 before:absolute before:inset-y-0 before:inset-s-0 before:w-px first:before:bg-transparent select-none" style={style}>
    <div className="flex items-center justify-start gap-2">
      <span className="grow truncate" {...attributes} {...listeners}>{isPlaceholder ? null : flexRender(column.columnDef.header, header.getContext())}</span>
      <Button size="icon" variant="ghost" className="group -mr-1 size-4 shadow-none"
        onClick={() => {
          const currentSort = isSorted
          if (currentSort === false) {
            column.toggleSorting(false)
          } else if (currentSort === "asc") {
            column.toggleSorting(true)
          } else {
            column.clearSorting()
          }
        }}
        onKeyDown={(e) => {
          if (column.getCanSort() &&
            (e.key === "Enter" || e.key === " ")) {
            e.preventDefault()
            const currentSort = isSorted
            if (currentSort === false) {
              column.toggleSorting(false)
            } else if (currentSort === "asc") {
              column.toggleSorting(true)
            } else {
              column.clearSorting()
            }
          }
        }}
      >
        {isSorted === "asc" && <ChevronUpIcon className="shrink-0 opacity-60" size={16} aria-hidden="true" />}
        {isSorted === "desc" && <ChevronDownIcon className="shrink-0 opacity-60" size={16} aria-hidden="true" />}
        {isSorted === false && <div className="shrink-0 opacity-0 group-hover:opacity-60" aria-hidden="true"><ChevronsUpDownIcon size={16} /></div>}
      </Button>
    </div>
    {column.getCanResize() && (
      <div
        {...{
          onDoubleClick: () => column.resetSize(),
          onMouseDown: header.getResizeHandler(),
          onTouchStart: header.getResizeHandler(),
          className: "absolute top-0 h-full w-4 cursor-col-resize user-select-none touch-none -right-2 z-10 flex justify-center before:absolute before:w-px before:inset-y-0 before:bg-border before:translate-x-px",
        }}
      />
    )}
  </TableHead>

}

function DragAlongCell({ cell }: { cell: Cell<any, unknown> }) {

  const { isDragging, setNodeRef, transform, transition } = useSortable({ id: cell.column.id, })

  const style: CSSProperties = {
    opacity: isDragging ? 0.8 : 1,
    position: "relative",
    transform: CSS.Translate.toString(transform),
    transition,
    width: cell.column.getSize(),
    zIndex: isDragging ? 1 : 0,
  }
  // console.log(cell.column.id, cell.column.getSize())


  return <TableCell ref={setNodeRef} className="truncate px-2 py-2" style={style}>
    {flexRender(cell.column.columnDef.cell, cell.getContext())}
  </TableCell>
}



const numberFormatter = new Intl.NumberFormat()

DataTable.cellRender = {
  date: ({ row, column }) => format(row.original[column.id], "MM/dd/yyyy hh:mm:ss"),
  json: ({ row, column }) => {
    const content = JSON.stringify(row.original[column.id], null, 2)
    return <Accordion>
      <AccordionItem value={column.id}>
        <AccordionTrigger className="bg-slate-100 p-2 overflow-hidden rounded-md" >Data</AccordionTrigger>
        <AccordionContent >
          <pre className="bg-slate-100 p-2 overflow-hidden rounded-md">{content}</pre>
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  },
  number: ({ row, column }) => {
    const value = Number(row.original[column.id]) || 0

    return <div className="tabular-nums text-left text-slate-600">{numberFormatter.format(value)}</div>
  },
  boolean: ({ row, column }) => {
    return row.original[column.id] ? <Check className="h-4 w-4 text-green-600" /> : null
  },
}
